import { useState } from "react";
import { Orbit as OrbitIcon } from "lucide-react";
import { useDashboard } from "@/components/layout/AppShell";
import { OrbitMap } from "@/components/OrbitMap";
import { PageHeader, EmptyState } from "@/components/PageState";
import { SourceBadge } from "@/components/SourceBadge";
import { InsightPanel } from "@/components/InsightPanel";
import { STATUS_META } from "@/lib/orbit";
import { Skeleton } from "@/components/ui/skeleton";

export default function BusinessMap() {
  const { data, loading } = useDashboard();
  const [selected, setSelected] = useState(null);

  if (loading) return <Skeleton className="h-96 rounded-2xl" />;

  if (!data?.has_data) {
    return (
      <div>
        <PageHeader eyebrow="Discover" title="Business Map" />
        <EmptyState icon={OrbitIcon} title="Your business map builds as you answer"
          whatsMissing="No assessment responses yet, so ORBIT has nothing to map."
          whyItMatters="The map turns fragmented information into a single view of your whole business — the foundation for every insight." />
      </div>
    );
  }

  const active = selected || data.domains[0];
  const meta = STATUS_META[active.status] || STATUS_META.Unknown;

  return (
    <div>
      <PageHeader eyebrow="Discover" title="Business Map"
        description="Your business at the centre of a ten-domain transformation system. Select a domain to understand it.">
        <SourceBadge type="responses" />
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="rounded-2xl border border-border bg-card p-6 lg:col-span-3">
          <div className="flex justify-center">
            <OrbitMap domains={data.domains} businessName={data.organisation_name} onSelect={setSelected} selectedKey={active.key} size={480} />
          </div>
        </div>

        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-2xl border-2 bg-card p-6" style={{ borderColor: meta.color }} data-testid="domain-detail">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">{active.label}</h3>
              <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}>
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta.color }} />{active.status}
              </span>
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-num text-3xl font-semibold" style={{ color: meta.color }}>{active.score}</span>
              <span className="font-num text-sm text-muted-foreground">/100</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{active.note}</p>
            {active.evidence?.length > 0 && (
              <div className="mt-4 border-t border-border pt-3" data-testid="domain-evidence">
                <div className="mb-2">
                  <SourceBadge type="responses" />
                </div>
                <ul className="space-y-2">
                  {active.evidence.map((e) => (
                    <li key={e.question} className="text-xs">
                      <span className="text-muted-foreground">{e.question}</span>
                      <span className="mt-0.5 block font-medium">→ {e.answer}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <InsightPanel key={active.key} operation="domain" payload={{ domain_key: active.key }} heading={`ORBIT Insight — ${active.label}`} variant="insight" testid="insight-domain" />

          <div className="rounded-2xl border border-border bg-card p-5">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">All domains</p>
            <ul className="space-y-1">
              {data.domains.map((d) => {
                const m = STATUS_META[d.status] || STATUS_META.Unknown;
                return (
                  <li key={d.key}>
                    <button onClick={() => setSelected(d)} className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors duration-200 hover:bg-muted ${active.key === d.key ? "bg-muted" : ""}`} data-testid={`domain-row-${d.key}`}>
                      <span className="flex items-center gap-2.5"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: m.color }} />{d.label}</span>
                      <span className="font-num text-xs text-muted-foreground">{d.score}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
